"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AnimatedOwl from "../home/_components/AnimatedOwl";
import LeftComponent from "../home/_components/LeftComponent";
import { ChevronRight, ChevronLeft, ChevronDown, Pencil, Palette, Monitor, Rainbow } from "lucide-react";
import { SCHOOLS, setActiveSchoolId, getTeachersForActiveSchool } from "../../services/backend/api";
import type { HardcodedTeacher } from "../../services/backend/api";

/** Which sub-view is active on the right side. */
type View = "main" | "teacher";
const DEFAULT_TEACHER_PASSWORD = "0000";
const TEACHER_PASSWORD_KEY_PREFIX = "teacherPassword:";

const getTeacherPassword = (teacherId: number): string =>
  localStorage.getItem(`${TEACHER_PASSWORD_KEY_PREFIX}${teacherId}`) ??
  DEFAULT_TEACHER_PASSWORD;

// ── Feature icons for the "Learn words by drawing them" panel ──
const FEATURE_ACTIVITIES = [
  { Icon: Pencil,  bg: "bg-orange-400", label: "Draw"    },
  { Icon: Palette, bg: "bg-green-400",  label: "Color"   },
  { Icon: Monitor, bg: "bg-blue-400",   label: "Digital" },
  { Icon: Rainbow, bg: "bg-purple-400", label: "Create"  },
];

const Login = () => {
  const router = useNavigate();

  const [view, setView] = useState<View>("main");
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | "">("");
  const [schoolError, setSchoolError] = useState("");
  const [teachers, setTeachers] = useState<HardcodedTeacher[]>([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState<number | "">("");
  const [teacherPassword, setTeacherPassword] = useState("");
  const [teacherError, setTeacherError] = useState("");

  const selectedSchool =
    selectedSchoolId === ""
      ? null
      : SCHOOLS.find((school) => school.schoolId === Number(selectedSchoolId)) ?? null;

  // ── Helpers ──

  /** Validates that a school is selected; sets selectedSchool in storage. */
  const confirmSchool = async (): Promise<boolean> => {
    if (!selectedSchoolId) {
      setSchoolError("Please select your school first.");
      return false;
    }
    setSchoolError("");
    await setActiveSchoolId(Number(selectedSchoolId));
    return true;
  };

  const handleStudentClick = async () => {
    if (await confirmSchool()) router("/login/student");
  };

  const handleTeacherClick = async () => {
    if (await confirmSchool()) {
      setTeachers(getTeachersForActiveSchool());
      setSelectedTeacherId("");
      setTeacherPassword("");
      setTeacherError("");
      setView("teacher");
    }
  };

  const getTeacherEmail = (teacher: HardcodedTeacher): string => {
    const username = teacher.teacherName
      .toLowerCase()
      .replace(/^(mr\.?|ms\.?|mrs\.?)\s+/i, "")
      .replace(/[^a-z]/g, "");
    return `${username}.${teacher.teacherId}@taim.school`;
  };

  const handleTeacherSignIn = async () => {
    if (!selectedTeacherId || !teacherPassword) {
      setTeacherError("Please fill in all fields.");
      return;
    }

    const teacher = teachers.find((item) => item.teacherId === Number(selectedTeacherId));
    if (!teacher) {
      setTeacherError("Selected teacher was not found.");
      return;
    }

    if (teacherPassword !== getTeacherPassword(teacher.teacherId)) {
      setTeacherError("Incorrect credentials.");
      return;
    }

    if (selectedSchoolId) {
      await setActiveSchoolId(Number(selectedSchoolId));
    }
    localStorage.setItem("activeTeacher", JSON.stringify(teacher));
    router("/teacher-dashboard");
  };

  // ── Render ──

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-300 via-pink-200 to-yellow-200 overflow-hidden relative">
      <div className="container mx-auto px-4 py-8 relative z-10">
        <AnimatedOwl />

        <div className="grid md:grid-cols-2 gap-8 items-center">
          <LeftComponent />

          <div className="space-y-5">

            {/* ── Main login view (floating, no outer card) ── */}
            {view === "main" && (
              <>
                {/* Title */}
                <h2 className="text-3xl font-bold text-purple-700 text-center">
                  · Log In ·
                </h2>

                {/* School dropdown */}
                <div className="relative">
                  <select
                    value={selectedSchoolId}
                    onChange={(e) => {
                      setSelectedSchoolId(e.target.value ? Number(e.target.value) : "");
                      setSchoolError(""); // clear error when user selects
                    }}
                    className={`w-full p-4 pr-10 rounded-2xl border-2 appearance-none bg-white text-gray-700 font-medium focus:outline-none transition-colors ${
                      schoolError
                        ? "border-red-400 focus:border-red-500"
                        : "border-purple-200 focus:border-purple-500"
                    }`}
                  >
                    <option value="">Select Your School</option>
                    {SCHOOLS.map((s) => (
                      <option key={s.schoolId} value={s.schoolId}>
                        {s.schoolName}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400 pointer-events-none" />
                </div>

                {/* Inline error — shown only when Student/Teacher clicked without a school */}
                {schoolError && (
                  <p className="text-red-500 font-medium text-sm text-center -mt-2">
                    ⚠️ {schoolError}
                  </p>
                )}

                {/* Student button */}
                <button
                  onClick={handleStudentClick}
                  className="w-full bg-gradient-to-r from-green-400 to-emerald-500 p-5 rounded-2xl shadow-lg cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                >
                  <div className="flex items-center space-x-4">
                    <span className="text-4xl">🎒</span>
                    <span className="text-xl font-bold text-white flex-grow text-left">Student</span>
                    <ChevronRight className="w-6 h-6 text-white" />
                  </div>
                </button>

                {/* Teacher button */}
                <button
                  onClick={handleTeacherClick}
                  className="w-full bg-gradient-to-r from-purple-400 to-violet-500 p-5 rounded-2xl shadow-lg cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                >
                  <div className="flex items-center space-x-4">
                    <span className="text-4xl">📋</span>
                    <span className="text-xl font-bold text-white flex-grow text-left">Teacher</span>
                    <ChevronRight className="w-6 h-6 text-white" />
                  </div>
                </button>

                {/* Guest link */}
                <p className="text-center text-gray-500 text-sm">
                  ✨ Just exploring?{" "}
                  <button
                    onClick={() => router("/login/guest")}
                    className="text-purple-600 font-semibold hover:underline cursor-pointer"
                  >
                    Continue as a Guest
                  </button>
                </p>

                {/* Feature panel — separate card below the buttons */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 shadow-xl text-center space-y-5">
                  <p className="text-purple-700 font-bold">Learn words by drawing them</p>
                  <div className="flex justify-center gap-4">
                    {FEATURE_ACTIVITIES.map(({ Icon, bg, label }, i) => (
                      <div
                        key={i}
                        title={label}
                        className={`${bg} p-4 rounded-2xl shadow-lg transform transition-all duration-300 hover:scale-110 hover:shadow-xl animate-bounce`}
                        style={{ animationDelay: `${i * 0.1}s` }}
                      >
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* ── Teacher sign-in ── */}
            {view === "teacher" && (
              <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/30 space-y-4">
                <h2 className="text-3xl font-bold text-purple-700 text-center mb-2">
                  Teacher Sign In
                </h2>

                <div>
                  <label className="block text-purple-700 font-bold text-lg mb-2">School</label>
                  <input
                    value={selectedSchool?.schoolName ?? ""}
                    readOnly
                    className="w-full p-3 rounded-xl border-2 border-purple-200 bg-purple-50 text-gray-700"
                  />
                </div>

                <div>
                  <label className="block text-purple-700 font-bold text-lg mb-2">Email</label>
                  <div className="relative">
                    <select
                      value={selectedTeacherId}
                      onChange={(e) => {
                        setSelectedTeacherId(e.target.value ? Number(e.target.value) : "");
                        setTeacherError("");
                      }}
                      className="w-full p-3 pr-10 rounded-xl border-2 border-purple-300 focus:border-purple-500 focus:outline-none text-gray-700 bg-white appearance-none"
                    >
                      <option value="">Choose your email...</option>
                      {teachers.map((teacher) => (
                        <option key={teacher.teacherId} value={teacher.teacherId}>
                          {getTeacherEmail(teacher)}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-purple-700 font-bold text-lg mb-2">Password</label>
                  <input
                    type="password"
                    value={teacherPassword}
                    onChange={(e) => {
                      setTeacherPassword(e.target.value);
                      setTeacherError("");
                    }}
                    placeholder="Enter password"
                    className="w-full p-3 rounded-xl border-2 border-purple-300 focus:border-purple-500 focus:outline-none text-gray-700"
                  />
                </div>

                {teacherError && (
                  <p className="text-red-500 font-medium text-sm text-center">
                    {teacherError}
                  </p>
                )}

                <button
                  onClick={handleTeacherSignIn}
                  className="w-full bg-gradient-to-r from-purple-500 to-violet-500 text-white text-lg font-bold py-3 rounded-2xl shadow-lg cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                >
                  Sign In
                </button>

                <button
                  onClick={() => setTeacherError("Forgot Password - TBD")}
                  className="w-full text-purple-600 text-sm font-medium hover:text-purple-800 transition-colors cursor-pointer"
                >
                  Forgot Password?
                </button>

                <button
                  onClick={() => {
                    setTeacherError("");
                    setTeacherPassword("");
                    setSelectedTeacherId("");
                    setView("main");
                  }}
                  className="w-full flex items-center justify-center gap-2 text-purple-600 font-medium hover:text-purple-800 transition-colors cursor-pointer mt-2"
                >
                  <ChevronLeft className="w-4 h-4" /> Back to Login
                </button>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;


